import { appConfig } from './../config'
import {
  ClampToEdgeWrapping,
  Color,
  Group,
  Material,
  MeshBasicMaterial,
  MeshNormalMaterial,
  MeshStandardMaterial,
  Object3D,
  Texture,
  TextureLoader,
} from 'three'
import { ConfigsType } from '../config'
import {
  DoorNewVisualTypes,
  DoorVisualType,
  DrawerVisualType,
} from '../redux/cabinetReducer'
import {
  createCylinderHandle,
  createDoorVGroove,
  createDrawer,
  createSheet,
  createSplittedTop,
  createStandardLegs,
  createDoorFivePieces,
  createSideSheetForBaseLegs,
  createSeparateBasePlatform,
  createCornerHorizontalSheet,
  createCornerTwoDoor,
  createCornerLegs,
  createStandard2Legs,
  createCornerLegs2,
  createArchHandle,
  createNewDoors,
  createShakerDoor,
  createStandard2Leg,
  createNewHandle,
} from './3DObjects'
import Hardware from './Hardware'
import NMapURL from './image.png'

/*
    Items' names rules

    1)Shelves

    ex. Shelf_1_2, Shelf_3_1

    name = 'Shelf' + '_' + sectionNumber + '_' + shelfNumber

    2)Fixed Shelves

    ex. Fixed Shelf 1, Fixed Shelf 3

    name = 'Fixed Shelf ' + dividerNumber

    3)Doors

    ex. Left Door 1, Double Door 3

    name = doorType + ' Door ' + sectionNumber

    4)Drawers

    ex. Drawer 1, Drawer 3

    name = 'Door ' + sectionNumber

    5)Legs

    if type is standard -> name = 'Standard Legs'
*/

export type LinkType = {
  hId: string
  hProp: string
  itemProp: string
}

export type AddonType = {
  dollar: number
  percentage: number
}

type ShelfItemPropsType = {
  type: 'shelf'
  length: number
  width: number
  thickness?: number
}

type SideSheetBaseLegPropsType = {
  type: 'sideSheetBaseLeg'
  width: number
  cabinetHeight: number
  legHeight: number
  thickness?: number
  cutLength?: number
  backCut?: number
  isNewBase?: boolean
}

type SectionItemPropsType = {
  type: 'section'
  width: number
  height: number
}

export type HandleOrientationType =
  | 'vertical'
  | 'horizontal'
  | 'double_horizontal'

type HandleItemPropsType = {
  type: 'handle'
  hPosition: 'left' | 'right' | 'center'
  orientation: HandleOrientationType
  subType: 'pull' | 'knob' | 'noPull'
  subType2?:
    | 'standard'
    | 'arch'
    | 'DHP504-76'
    | 'DHP306-96'
    | 'DHP180-128'
    | 'DHP465-192'
    | 'DHP305MB96'
    | 'DHP305MB128'
    | 'BP723812'
    | 'BP873128'
    | 'DHK301'
    | 'DHK401'
  for?: 'door' | 'drawer'
  position:
    | 'top'
    | 'bottom'
    | 'center'
    | 'fromGround'
    | 'forDrawer'
    | 'forDrawerCenter'
    | number
}

export type DoorItemPropsType = {
  type: 'door'
  visualType: DoorVisualType
  subType: 'left' | 'right' | 'double' | 'liftUp'
  sectionType: 'first' | 'last' | 'middle' | 'single'
  width: number
  height: number
  thickness?: number
  splitters?: string
  glassHeight?: number
  mirrorHeight?: number
  frostedHeight?: number
  holeWidth?: number
}

export type FivePiecesDoorItemPropsType = {
  type: 'fivePiecesDoor'
  visualType: 'fivePieces'
  subType: 'left' | 'right' | 'double' | 'liftUp'
  sectionType: 'first' | 'last' | 'middle' | 'single'
  width: number
  height: number
  thickness?: number
  splitters?: string
  sheetHeight: number
  glassHeight: number
  cleanHeight: number
  mirrorHeight: number
  frostedHeight: number
  holeWidth: number
  piecesLength: number
  piecesWidth: number
}

export type NewDoorItemPropsType = {
  type: 'newDoor'
  visualType: DoorNewVisualTypes
  subType: 'left' | 'right' | 'double' | 'liftUp'
  sectionType: 'first' | 'last' | 'middle' | 'single'
  width: number
  height: number
  thickness?: number
  splitters?: string
  glassHeight: number
  cleanHeight: number
  mirrorHeight: number
  frostedHeight: number
  holeWidth: number
}

export type CornerTwoDoorItemPropsType = {
  type: 'cornerTwoDoor'
  visualType: DoorVisualType
  subType: 'left' | 'right'
  sectionType: 'first' | 'last' | 'middle' | 'single'
  width1: number
  width2: number
  width: number
  height: number
  thickness: number
  splitters?: string
}

export type FivePiecesCornerTwoDoorItemPropsType = {
  type: 'cornerTwoDoorFivePieces'
  visualType: 'fivePieces'
  subType: 'left' | 'right'
  sectionType: 'first' | 'last' | 'middle' | 'single'
  width1: number
  width2: number
  height: number
  thickness: number
  splitters?: string
  sheetHeight: number
  glassHeight: number
  cleanHeight: number
  mirrorHeight: number
  frostedHeight: number
  holeWidth: number
  piecesLength: number
  piecesWidth: number
}

export type NewDoorCornerTwoDoorItemPropsType = {
  type: 'cornerNewDoor'
  visualType: DoorNewVisualTypes
  subType: 'left' | 'right'
  sectionType: 'first' | 'last' | 'middle' | 'single'
  width1: number
  width2: number
  width: number
  height: number
  thickness: number
  splitters?: string
  glassHeight: number
  cleanHeight: number
  mirrorHeight: number
  frostedHeight: number
  holeWidth: number
}

export type DrawerItemPropsType = {
  type: 'drawer'
  sectionType: 'first' | 'last' | 'middle' | 'single'
  visualType?: DrawerVisualType
  width: number
  height: number
  depth: number
  boxWidth: number
  boxHeight: number
  boxDepth: number
  thickness?: number
  // handleProps: HandleItemPropsType
}

export type FivePiecesDrawerItemPropsType = {
  type: 'fivePiecesDrawer'
  visualType: 'fivePieces'
  sectionType: 'first' | 'last' | 'middle' | 'single'
  width: number
  height: number
  depth: number
  boxWidth: number
  boxHeight: number
  boxDepth: number
  thickness?: number
  piecesArea: number
  centerHeight: number
  centerWidth: number
}

export type LegsItemPropsType = {
  type: 'leg'
  subType: 'standard'
  cabinetWidth: number
  cabinetDepth: number
  height: number
}

export type Legs2ItemPropsType = {
  type: 'leg2'
  subType: 'short' | 'tall'
  cabinetWidth: number
  cabinetDepth: number
  height: number
}

export type CornerOneLegItemPropsType = {
  type: 'cornerOneLeg'
  height: number
  width1: number
  width2: number
  backWidth1: number
  backWidth2: number
  depth1: number
  depth2: number
  thickness: number
}

export type CornerLegsItemPropsType = {
  type: 'cornerLegs'
  height: number
  width1: number
  width2: number
  backWidth1: number
  backWidth2: number
  depth1: number
  depth2: number
  thickness: number
}

export type CornerLegs2ItemPropsType = {
  type: 'cornerLegs2'
  subType: 'short' | 'tall'
  height: number
  width1: number
  width2: number
  backWidth1: number
  backWidth2: number
  depth1: number
  depth2: number
  thickness: number
}

export type CornerHorizontalSheetType = {
  type: 'cornerHorizontalSheet'
  subType: '2door' | 'angled'
  width1: number
  width2: number
  backWidth1: number
  backWidth2: number
  depth1: number
  depth2: number
  thickness: number
}

type SeparateBaseItemPropsType = {
  type: 'separateBase'
  width: number
  depth: number
  height: number
}

type SplittedTopItemPropsType = {
  type: 'splittedTop'
  cabinetWidth: number
  cabinetDepth: number
  sheetWidth: number
}

type AdditionalItemPropsType = {
  type: 'additional'
  [key: string]: number | string
}

// type ItemTypes = 'shelf' | 'section' | 'leftDoor'

type PropsType =
  | ShelfItemPropsType
  | SideSheetBaseLegPropsType
  | SeparateBaseItemPropsType
  | SectionItemPropsType
  | DoorItemPropsType
  | NewDoorItemPropsType
  | FivePiecesDoorItemPropsType
  | HandleItemPropsType
  | DrawerItemPropsType
  | FivePiecesDrawerItemPropsType
  | LegsItemPropsType
  | SplittedTopItemPropsType
  | AdditionalItemPropsType
  | CornerHorizontalSheetType
  | CornerTwoDoorItemPropsType
  | FivePiecesCornerTwoDoorItemPropsType
  | NewDoorCornerTwoDoorItemPropsType
  | CornerLegsItemPropsType
  | CornerLegs2ItemPropsType
  | CornerOneLegItemPropsType
  | Legs2ItemPropsType

export type ItemPropsType = {
  name: string
  uuid?: string
  props: PropsType
  matrix: number[] //Matrix4
  hardwaresProps: {
    id: number
    localId: number
    count: number
  }[]
  addon?: AddonType
  links: LinkType[]
  childrenProps: ItemPropsType[]
  render: boolean
}

const cabinetBoxSheetsNames = [
  'Top',
  'Bottom',
  'Back',
  'Right Side',
  'Left Side',
  'Right Side Base Leg',
  'Left Side Base Leg',
  'Splitted Top',
  'Front Left Side',
  'Front Right Side',
  'Back Left Side',
  'Back Right Side',
]

const cabinetShelfSheetsStartNames = ['Fixed Shelf', 'Shelf']

let nMap = undefined as undefined | Texture

;(async () => {
  const textLoader = new TextureLoader()
  nMap = await textLoader.load(NMapURL)
})()

export default class Item {
  name: string
  object3D: Object3D
  props: PropsType

  hardwares: Hardware[]
  addon: AddonType
  links: LinkType[]
  children: Item[] = []

  parent: Item | undefined

  configs: ConfigsType
  // setItemsPropsList: (arr: ItemPropsType[]) => void

  constructor(
    name: string,
    props: PropsType,
    hardwares: Array<Hardware> = [],
    links: LinkType[] = [],
    addon: AddonType = { dollar: 0, percentage: 0 },
    configs: ConfigsType,
    parent?: Item
  ) {
    this.name = name
    this.props = props
    this.hardwares = hardwares
    this.links = links
    this.addon = addon
    this.configs = configs
    // this.setItemsPropsList = setItemsPropsList

    this.parent = parent

    this.name += this.parent ? `$$${this.parent.name}` : ''

    this.object3D = this.generateObject3D()
    this.updateHardwareProps()
  }

  generateObject3D = () => {
    let texture = undefined
    let tLength = 1
    let tWidth = 1
    let materialId = -1
    let materialThreeProps = ''

    for (const hardware of this.hardwares) {
      if (hardware.materialBased && hardware.material) {
        // debugger
        // console.log('texture need to be used ' + hardware.material.name)
        texture = hardware.material.texture.clone()
        tLength = hardware.material.tLength
        tWidth = hardware.material.tWidth
        materialId = hardware.material.id
        materialThreeProps = hardware.material.threeProps
        // console.log(hardware.material, hardware)

        break
      }
    }

    const material = texture
      ? new MeshStandardMaterial({ map: texture })
      : undefined

    if (this.props.type === 'shelf') {
      if (texture) {
        texture.repeat.set(
          this.props.length / tLength,
          this.props.width / tWidth
        )
        texture.needsUpdate = true
      }

      let isPewterMat = false
      let materials: Material[] = []

      if (materialId === 76 && material) {
        //Pewter Brush
        if (
          cabinetBoxSheetsNames.includes(this.name) ||
          cabinetShelfSheetsStartNames.some((n) => this.name.includes(n))
        ) {
          const blackMat = new MeshBasicMaterial({ color: 'black' })
          isPewterMat = true
          materials = [
            blackMat,
            blackMat,
            blackMat,
            blackMat,
            material,
            material,
          ]
        }
      }

      const mesh = createSheet(
        this.props.length,
        this.props.width,
        this.configs.thickness,
        isPewterMat ? materials : material
      )

      mesh.name = this.name
      return mesh
    } else if (this.props.type === 'sideSheetBaseLeg') {
      const mesh = createSideSheetForBaseLegs(
        this.props.cabinetHeight,
        this.props.legHeight,
        this.props.width,
        this.configs.thickness,
        texture,
        tLength,
        tWidth,
        this.configs.baseLegConfig,
        this.props.isNewBase,
        this.props.cutLength,
        this.props.backCut
      )

      mesh.name = this.name
      return mesh
    } else if (this.props.type === 'door') {
      if (texture) {
        texture.repeat.set(
          this.props.width / tLength,
          this.props.height / tWidth
        )
        texture.needsUpdate = true
      }
      // if (this.props.subType === 'liftUp' && material) {
      //   // nMap.wrapS = nMap.wrapT = ClampToEdgeWrapping
      //   // nMap.repeat.set(1, 1)
      //   nMap.needsUpdate = true
      //   material.normalMap = nMap
      //   material.needsUpdate = true
      // }

      const door = this.props.visualType.startsWith('shaker')
        ? createShakerDoor(
            this.props.subType,
            this.props.visualType === 'shaker_v',
            this.props.width,
            this.props.height,
            this.configs.thickness,
            texture,
            tLength,
            tWidth,
            this.configs.doorConfig,
            materialThreeProps
          )
        : createDoorVGroove(
            this.props.subType,
            this.props.width,
            this.props.height,
            this.configs.thickness,
            texture,
            tLength,
            tWidth,
            this.configs.doorConfig,
            this.configs.vGrooveConfig,
            materialThreeProps
          )

      door.name = this.name
      door.userData.type = this.props.subType
      door.userData.isDoor = true

      return door
    } else if (this.props.type === 'newDoor') {
      if (texture) {
        texture.repeat.set(
          this.props.width / tLength,
          this.props.height / tWidth
        )
        texture.needsUpdate = true
      }
      // if (this.props.subType === 'liftUp' && material) {
      //   // nMap.wrapS = nMap.wrapT = ClampToEdgeWrapping
      //   // nMap.repeat.set(1, 1)
      //   nMap.needsUpdate = true
      //   material.normalMap = nMap
      //   material.needsUpdate = true
      // }

      // console.log(this.props.visualType)

      const door = createNewDoors(
        this.props.subType,
        this.props.visualType as DoorNewVisualTypes,
        this.props.splitters || '',
        this.props.width,
        this.props.height,
        this.configs.thickness,
        texture,
        tLength,
        tWidth,
        this.configs.doorConfig,
        false,
        materialThreeProps
      )

      door.name = this.name
      door.userData.type = this.props.subType
      door.userData.isDoor = true

      return door
    } else if (this.props.type === 'fivePiecesDoor') {
      if (texture) {
        texture.repeat.set(
          this.props.width / tLength,
          this.props.height / tWidth
        )
        texture.needsUpdate = true
      }
      // if (this.props.subType === 'liftUp' && material) {
      //   // nMap.wrapS = nMap.wrapT = ClampToEdgeWrapping
      //   // nMap.repeat.set(1, 1)
      //   nMap.needsUpdate = true
      //   material.normalMap = nMap
      //   material.needsUpdate = true
      // }

      // console.log(this.props.visualType)

      const door = createDoorFivePieces(
        this.props.subType,
        this.props.splitters || '',
        this.props.width,
        this.props.height,
        this.configs.thickness,
        texture,
        tLength,
        tWidth,
        this.configs.doorConfig,
        this.configs.fivePiecesConfig,
        false,
        false,
        materialThreeProps
      )
      door.name = this.name
      door.userData.type = this.props.subType
      door.userData.isDoor = true

      return door
    } else if (
      this.props.type === 'drawer' ||
      this.props.type === 'fivePiecesDrawer'
    ) {
      if (texture) {
        texture.repeat.set(
          this.props.width / tLength,
          this.props.height / tWidth
        )
        texture.needsUpdate = true
      }

      let boxTexture = undefined
      let btLength = 0
      let btWidth = 1
      const boxHardware = this.hardwares.find((h) =>
        h.name.startsWith('Drawer Box')
      )

      if (boxHardware && boxHardware.materialBased && boxHardware.material) {
        boxTexture = boxHardware.material.texture.clone()
        btLength = boxHardware.material.tLength
        btWidth = boxHardware.material.tWidth
      }

      const drawer = createDrawer(
        this.props.visualType || 'standard',
        this.props.width,
        this.props.height,
        this.props.depth,
        this.props.boxWidth,
        this.props.boxHeight,
        this.props.boxDepth,
        this.configs.thickness,
        texture,
        tLength,
        tWidth,
        boxTexture,
        btLength,
        btWidth,
        this.configs.drawerConfig,
        this.configs.doorConfig,
        this.configs.fivePiecesConfig,
        materialThreeProps
      )

      drawer.name = this.name
      // door.userData.type = this.props.subType
      drawer.userData.isDrawer = true

      return drawer
    } else if (this.props.type === 'handle') {
      let color = undefined

      if (texture && texture.name.startsWith('color||')) {
        color = new Color(texture.name.slice(7))
      }

      let handle = new Object3D()
      if (this.props.subType === 'noPull') {
        // console.log('handle', this.props)
        handle = createCylinderHandle(
          this.props.hPosition,
          this.props.orientation,
          this.configs.handleConfig,
          color,
          true
        )
      } else if (this.props.subType === 'pull') {
        if (this.props.subType2 === 'standard') {
          handle = createCylinderHandle(
            this.props.hPosition,
            this.props.orientation,
            this.configs.handleConfig,
            color,
            false,
            materialThreeProps
          )
        } else if (this.props.subType2 === 'arch') {
          handle = createArchHandle(
            this.props.hPosition,
            this.props.orientation,
            color,
            materialThreeProps
          )
        } else {
          handle = createNewHandle(
            this.props.subType2,
            this.props.hPosition,
            this.props.orientation,
            color,
            materialThreeProps
          )
        }
      } else {
        console.log('materialThreeProps', materialThreeProps)
        handle = createNewHandle(
          this.props.subType2 || 'DHK301',
          this.props.hPosition,
          this.props.orientation,
          color,
          materialThreeProps
        ) // createKnobHandle(this.props.hPosition, color)
      }

      handle.name = this.name
      return handle
    } else if (this.props.type === 'leg') {
      if (this.props.subType === 'standard') {
        const legs = createStandardLegs(
          this.props.height,
          this.props.cabinetWidth,
          this.props.cabinetDepth,
          this.configs.standardLegConfig
        )

        legs.name = 'Standard Legs'
        return legs
      }
    } else if (this.props.type === 'leg2') {
      const legs = createStandard2Legs(
        this.props.subType,
        this.props.height,
        this.props.cabinetWidth,
        this.props.cabinetDepth,
        this.configs.standard2LegConfig
      )

      legs.name =
        'Standard Legs 2 ' + (this.props.subType === 'short' ? 'Short' : 'Tall')
      return legs
    } else if (this.props.type === 'splittedTop') {
      if (texture) {
        texture.repeat.set(
          this.props.sheetWidth / tLength,
          this.props.cabinetWidth / tWidth
        )
        texture.needsUpdate = true
      }

      let isPewterMat = false
      let materials: Material[] = []

      if (materialId === 76 && material) {
        //Pewter Brush
        if (
          cabinetBoxSheetsNames.includes(this.name) ||
          cabinetShelfSheetsStartNames.some((n) => this.name.includes(n))
        ) {
          const blackMat = new MeshBasicMaterial({ color: 'black' })
          isPewterMat = true
          materials = [
            blackMat,
            blackMat,
            material,
            material,
            blackMat,
            blackMat,
          ]
        }
      }

      const splittedTop = createSplittedTop(
        this.props.cabinetWidth,
        this.props.cabinetDepth,
        this.props.sheetWidth,
        this.configs.thickness,
        isPewterMat ? materials : material
      )

      splittedTop.name = this.name
      return splittedTop
    } else if (this.props.type === 'separateBase') {
      const platform = createSeparateBasePlatform(
        this.props.width,
        this.props.depth,
        this.props.height,
        texture,
        tLength,
        tWidth,
        this.configs.separateBaseConfig
      )

      platform.name = this.name
      return platform
    } else if (this.props.type === 'cornerHorizontalSheet') {
      let isPewterMat = false

      if (materialId === 76 && material) {
        //Pewter Brush
        isPewterMat = true
      }

      const sheet = createCornerHorizontalSheet(
        this.props.subType,
        this.props.width1,
        this.props.width2,
        this.props.depth1,
        this.props.depth2,
        this.props.backWidth1,
        this.props.backWidth2,
        this.props.thickness,
        false,
        texture,
        tLength,
        tWidth,
        isPewterMat
      )

      sheet.name = this.name
      return sheet
    } else if (
      this.props.type === 'cornerTwoDoor' ||
      this.props.type === 'cornerTwoDoorFivePieces' ||
      this.props.type === 'cornerNewDoor'
    ) {
      // const door = this.props.visualType === 'fivePieces'
      // ? createDoorFivePieces(this.props.subType, this.props.width, this.props.height, this.configs.thickness, texture, tLength, tWidth, this.configs.doorConfig, this.configs.fivePiecesConfig)
      // : createDoorVGroove(this.props.subType, this.props.width, this.props.height, this.configs.thickness, texture, tLength, tWidth, this.configs.doorConfig, this.configs.vGrooveConfig)
      const door = createCornerTwoDoor(
        this.props.visualType,
        this.props.splitters || '',
        this.props.subType,
        this.props.width1,
        this.props.width2,
        this.props.height,
        this.props.thickness,
        texture,
        tLength,
        tWidth,
        this.configs,
        materialThreeProps
      )

      door.name = this.name
      door.userData.type = this.props.subType
      door.userData.isDoor = true

      return door
    } else if (this.props.type === 'cornerOneLeg') {
      const g = createStandard2Leg(
        'short',
        this.props.height,
        this.configs.standard2LegConfig
      )

      g.position.x = this.props.backWidth2 / 2 + 5
      g.position.z = this.props.backWidth1 / 2 + 5

      g.name = this.name

      return g
    } else if (this.props.type === 'cornerLegs') {
      const legs = createCornerLegs(
        this.props.height,
        this.props.width1,
        this.props.width2,
        this.props.depth1,
        this.props.depth2,
        this.props.backWidth1,
        this.props.backWidth2,
        this.props.thickness,
        this.configs.standardLegConfig
      )
      legs.name = 'Corner Standard Legs'

      return legs
    } else if (this.props.type === 'cornerLegs2') {
      const legs = createCornerLegs2(
        this.props.subType,
        this.props.height,
        this.props.width1,
        this.props.width2,
        this.props.depth1,
        this.props.depth2,
        this.props.backWidth1,
        this.props.backWidth2,
        this.props.thickness,
        this.configs.standard2LegConfig
      )
      legs.name =
        'Corner Standard Legs 2 ' +
        (this.props.subType === 'short' ? 'Short' : 'Tall')

      return legs
    }
    // else if (this.props.type === 'additional') {
    // }

    return new Object3D()
  }

  updateHardwareProps = () => {
    for (const link of this.links) {
      let hardware = undefined as undefined | Hardware

      if (link.hId.split('_').length > 1) {
        const ids = link.hId
          .split('_')
          .map((id) => parseInt(id))
          .reverse()

        hardware = this.hardwares.find((h) => h.localId === ids[0])

        for (let i = 1; i < ids.length; i++) {
          hardware = hardware?.children.find((h) => h.localId === ids[i])
        }
      } else {
        const id = parseInt(link.hId)

        hardware = this.hardwares.find((h) => h.localId === id)
      }

      if (hardware) {
        const newProps = { ...hardware.props }

        if (link.hProp.split('_')[0] === 'count') {
          const perCM = link.hProp.split('_')[1] ? link.hProp.split('_')[1] : 1

          for (const key in newProps) {
            if (key.split('_')[0] === 'count') {
              // @ts-ignore
              delete newProps[key]
            }
          }

          // @ts-ignore
          newProps[link.hProp] = {
            // @ts-ignore
            value: Math.floor(this.props[link.itemProp] / perCM),
            extra: 0,
          }
        } else if (link.hProp === 'subType') {
          // @ts-ignore
          newProps[link.hProp] = this.props[link.itemProp]
        } else {
          // @ts-ignore
          newProps[link.hProp].value = this.props[link.itemProp]
        }
        hardware.updateProps(newProps)
      }
    }
  }

  // updateProps = (props: PropsType, itemsProps: ItemPropsType[]) => {

  //     const item = itemsProps.find(item => item.name === this.name)

  //     if (item) {
  //         item.props = props
  //     }

  //     this.setItemsPropsList(itemsProps)
  //     // this.props = props
  //     // this.object3D = this.generateObject3D()
  //     // this.updateHardwareProps()
  // }

  addChild = (child: Item) => {
    this.children.push(child)
  }

  getPrice = () => {
    let sum = 0

    this.hardwares.forEach((h) => {
      h.calculatePrice()
      sum += h.price
    })

    for (const child of this.children) {
      sum += parseFloat(child.getPrice())
    }

    return (
      (sum + this.addon.dollar) *
      (1 + this.addon.percentage / 100)
    ).toFixed(appConfig.fractionDigits)
  }

  getClearPrice = () => {
    let sum = 0

    this.hardwares.forEach((h) => {
      h.calculatePrice()
      sum += h.clearPrice
    })

    for (const child of this.children) {
      sum += parseFloat(child.getClearPrice())
    }

    return sum.toFixed(appConfig.fractionDigits)
  }

  getWeight = () => {
    let sum = 0

    this.hardwares.forEach((h) => {
      h.calculatePrice()
      sum += h.weight
    })

    for (const child of this.children) {
      sum += parseFloat(child.getWeight())
    }

    return sum.toFixed(appConfig.fractionDigits)
  }

  getNonMarkupPrice = () => {
    let sum = 0

    this.hardwares.forEach((h) => {
      h.calculatePrice()
      sum += h.nonMarkupPrice
    })

    for (const child of this.children) {
      sum += parseFloat(child.getNonMarkupPrice())
    }

    return (
      (sum + this.addon.dollar) *
      (1 + this.addon.percentage / 100)
    ).toFixed(appConfig.fractionDigits)
  }
}
